import { create } from 'zustand';
import type { HandId } from './handStore';

/** Envelope shape that drives the glow's fade — mirrors the audio
 *  PluckVoice envelope so the glow dies when the string dies. */
export interface FlashEnvelope {
  attackMs: number;
  decayMs: number;
  sustainFrac: number;
  tailSec: number;
}

export interface Flash {
  at: number; // performance.now()
  velocity: number; // 0..1
  /** Which hand plucked this string — drives glow colour so the
   *  player can see at a glance which hand hit what. */
  hand: HandId | null;
  /** Envelope params captured at pluck time so the visual decay
   *  matches the audio decay for THIS pluck's preset (cheaper than
   *  reading the live active slot every frame, and correct if the
   *  user swaps tones while a note is still ringing). */
  env: FlashEnvelope;
}

interface FlashState {
  /** Keyed by stringIndex. Last pluck event per string. */
  flashes: Record<number, Flash>;
  flash: (
    stringIndex: number,
    velocity: number,
    now: number,
    hand: HandId | null,
    env: FlashEnvelope,
  ) => void;
}

/**
 * Ephemeral pluck flash state — consumed by `StringsOverlay` to render
 * a velocity-scaled glow that fades over ~450ms.
 *
 * Read via `useFlashStore.getState().flashes` inside the rAF draw loop
 * (no subscription needed; rAF drives the frame cadence).
 */
export const useFlashStore = create<FlashState>((set) => ({
  flashes: {},
  flash: (stringIndex, velocity, now, hand, env) =>
    set((s) => ({
      flashes: {
        ...s.flashes,
        [stringIndex]: { at: now, velocity, hand, env },
      },
    })),
}));
